/* eslint-disable @typescript-eslint/naming-convention */

import React, { createContext, useContext } from 'react';
import type { Events, ListPageEntry, RemoteModuleRegistry } from '@dutchiesdk/ecommerce-extensions-sdk';

import type { CategoryPageSubheaderEntry } from 'src/dispensary/category/category-page-subheaders';
import type { RemoteModuleRegistryWithSpecialsPageHeader } from 'src/dispensary/specials/specials-page-header';

// TEMPORARY: @dutchiesdk/ecommerce-extensions-sdk doesn't export CustomTagFilterConfig /
// RemoteModuleRegistry.enableCustomTagFilters yet -- the companion SDK PR
// (GetDutchie/ecommerce-extensions-sdk#102) is still open. Defined locally, field-for-field
// with that PR's diff, so this compiles against the currently-published SDK. Once that PR
// ships and apps/marketplace/package.json is bumped to a version that includes it, delete
// this local type and RemoteModuleRegistryWithCustomTagFilters below, and import both
// CustomTagFilterConfig and the (by-then-updated) RemoteModuleRegistry from the SDK instead.
export type CustomTagFilterConfig = {
  /** Top-level category key the filter renders on (e.g. `'pre-rolls'`, `'flower'`). */
  category: string;
  /** Arbitrary label for the theme developer's own reference; not shown to shoppers. */
  name: string;
  /** Filter section heading shown to shoppers. */
  title: string;
  /**
   * Substrings matched case-insensitively against POS canonical tag names, e.g.
   * `['ct', 'count']` matches `'1ct'` and `'2 count'`.
   */
  match: string[];
};

/** RemoteModuleRegistry, plus the not-yet-published enableCustomTagFilters field. See above. */
export type RemoteModuleRegistryWithCustomTagFilters = RemoteModuleRegistry & {
  enableCustomTagFilters?: CustomTagFilterConfig[];
};

type FallbackProps = { fallback?: React.FC<any> };
type DefaultComponentSig = (options?: FallbackProps) => React.FC<any>;
type CategoryIterable = { category: ListPageEntry['category']; components: React.ElementType[] };

export type RouteablePage = {
  path: string;
  component: DefaultComponentSig;
};

// The `theme` prop forwarded verbatim to remote (module-federation) components — see
// remote-theme.tsx's `wrapComponentFactory` and README.md's "Accept and use the data and theme
// props". Today it's always the styled-components/MUI theme object built by `getStyledTheme`
// (theme/builders/styled.js, untyped JS) — never a string — but nothing in this subsystem reads
// any of its properties, so it's typed as an opaque plain object rather than that builder's
// specific (currently unnamed) shape.
export type RemoteTheme = Record<string, unknown> | null;

export type ThemeConfig = {
  customTagFilters: CustomTagFilterConfig[];
};

export type CommerceComponents = {
  components: {
    [K in keyof Omit<
      RemoteModuleRegistryWithCustomTagFilters & RemoteModuleRegistryWithSpecialsPageHeader,
      | 'CategoryPageInterstitials'
      | 'CategoryPageSlots'
      | 'enableCustomTagFilters'
      | 'events'
      | 'getStoreFrontMetaFields'
      | 'RouteablePages'
      | 'StoreFrontCarouselInterstitials'
    >]-?: DefaultComponentSig;
  };
  findPage: (o: { fallback?: React.FC; path?: string }) => React.FC;
  iterables: {
    StoreFrontCarouselInterstitials: () => React.ElementType[];
    CategoryPageInterstitials: () => CategoryIterable[];
    CategoryPageSlots: () => CategoryIterable[];
    /**
     * Not a component -- raw, unresolved SDK-authored entries for the
     * optional category page sub-header (ECOMM-13289). Consumers should
     * resolve/validate the matching entry via
     * `findMatchingCategorySubheaderText` rather than reading `text` off
     * these directly.
     */
    CategoryPageSubheaders: () => CategoryPageSubheaderEntry[];
  };
  pages: ({ fallback }: FallbackProps) => RouteablePage[];
  events: Events | undefined;
  isRemoteThemeResolved: boolean;
  themeConfig: ThemeConfig;
};

const EmptyComponent: React.FC<any> = () => <></>;

export const defaultCommerceComponents: CommerceComponents = {
  components: {
    StoreFrontHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontNavigation: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontFooter: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    GlobalStyles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoyaltyWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoginWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ListPageHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsPrimary: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCardListItem: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    FilterSidePanel: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsPageHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
  },
  findPage: ({ fallback = () => <></> }) => fallback,
  iterables: {
    StoreFrontCarouselInterstitials: () => [],
    CategoryPageInterstitials: () => [],
    CategoryPageSlots: () => [],
    CategoryPageSubheaders: () => [],
  },

  pages: (_props: FallbackProps = { fallback: () => <></> }) => [],
  events: {},
  isRemoteThemeResolved: false,
  themeConfig: { customTagFilters: [] },
};

export const CommerceComponentsContext = createContext<CommerceComponents>(defaultCommerceComponents);

export const useCommerceComponents = (): CommerceComponents => useContext(CommerceComponentsContext);
